import { useEffect, useState } from 'react';
import moment from 'moment';
import helper from '../../utils/helper';
import { errorMessage } from '../../utils/commonStringsConsts';
import { getUserBanksApi, createBulkUsersAPI } from '../../utils/apisConsts';

import { OutPutResult, Spinner, MaxInstallmentsWarningModal } from '@operations/shared';
import { getRegion } from '../../components/regionWrapper';
import { Region } from '../../utils/commonInterfaces';
import {
  Form,
  Input,
  Select,
  Button,
  DatePicker,
  message,
  Card,
  Row,
  Col,
  Typography,
  InputNumber,
  Modal,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  IdcardOutlined,
  MailOutlined,
  HomeOutlined,
  DollarOutlined,
  BankOutlined,
  NumberOutlined,
  TeamOutlined,
  CalendarOutlined,
  SolutionOutlined,
  FieldTimeOutlined,
  SaveOutlined,
  ReloadOutlined,
  UserAddOutlined,
  CreditCardOutlined,
  WarningFilled,
  CloseCircleFilled,
  CloseOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { Title, Text } = Typography;

export default function CreateUser() {
  const [loader, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMode, setErrorMode] = useState(false);
  const [errorsList, setErrorsList] = useState([]);
  const [banks, setBanks] = useState([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [warningModal, setWarningModal] = useState<{ visible: boolean; companyMax?: number; pendingData?: any }>({ visible: false });
  const region = getRegion();

  const [createUserFormInstance] = Form.useForm();
  const validationPatterns: any = helper.regexValidation;

  // Company Max Installment Count of the currently selected company (used for the warning)
  const selectedCompanyName = Form.useWatch('companyName', createUserFormInstance);
  const getCompanyMaxInstallmentCount = (companyName: string) => {
    const selectedCompany = Array.isArray(companies)
      ? companies.find((company: any) => company.name === companyName)
      : undefined;
    return selectedCompany?.maxInstallmentCount ? Number(selectedCompany.maxInstallmentCount) : undefined;
  };
  const companyMaxInstallmentCount = getCompanyMaxInstallmentCount(selectedCompanyName);

  const egUserFields = [
    'phone', 'name', 'bankName', 'accountHolderName', 'accountNumber', 'netSalary', 'companyName', 'email', 'nationalID', 'staff_id', 'comment',
  ];
  const seUserFields = [
    'phone', 'name', 'bankName', 'accountHolderName', 'accountNumber', 'netSalary', 'companyName', 'email', 'nationalID', 'staff_id',
  ];
  const userFields = region === Region.EG ? egUserFields : seUserFields;

  useEffect(() => {
    fetchRequiredData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Functions ----------------------------------------------------------
  const fetchRequiredData = async () => {
    try {
      setLoader(true);
      const [companiesData, banksRes]: any[] = await Promise.all([
        helper.getAllCompanies(),
        helper.callApiRequest(getUserBanksApi, 'GET'),
      ]);

      setLoader(false);
      if ((companiesData.status !== 200) || (banksRes.data.status !== 200)) {
        setErrorMode(true);
        return;
      }

      setCompanies(companiesData.data);
      setBanks(banksRes.data.banks);
    } catch (error) {
      setErrorMode(true);
      setLoader(false);
    }
  };

  const submitForm = async (userData: any) => {
    const enteredMaxInstallments = userData.userMaxInstallments;
    const companyMax = getCompanyMaxInstallmentCount(userData.companyName);
    const hasEnteredMaxInstallments = enteredMaxInstallments !== undefined && enteredMaxInstallments !== null && enteredMaxInstallments !== '';

    // Warn if the user is being granted more installments than his company's maximum
    if (hasEnteredMaxInstallments && companyMax && Number(enteredMaxInstallments) > companyMax) {
      setWarningModal({ visible: true, companyMax, pendingData: userData });
      return;
    }

    proceedSubmit(userData);
  };

  const handleWarningCancel = () => setWarningModal({ visible: false });

  const handleWarningConfirm = () => {
    const pendingData = warningModal.pendingData;
    setWarningModal({ visible: false });
    if (pendingData) proceedSubmit(pendingData);
  };

  const proceedSubmit = async (userData: any) => {
    setSaving(true);

    try {
      const formalizedUserData = {
        updatedFields: [...userFields],
        sheetData: [{
          ...userData,
          hiringDate: userData.hiringDate ? helper.changeDateFormat(userData.hiringDate, 'YYYY/MM/DD 12:00:00') : '',
        }],
        defaultSignUp: true,
      };
      userData.hiringDate && formalizedUserData.updatedFields.push('hiringDate');
      userData.jobTitle && formalizedUserData.updatedFields.push('jobTitle');
      userData.accountHolderNameEn && formalizedUserData.updatedFields.push('accountHolderNameEn');
      const hasMaxInstallments = userData.userMaxInstallments !== undefined && userData.userMaxInstallments !== null && userData.userMaxInstallments !== '';
      hasMaxInstallments && formalizedUserData.updatedFields.push('userMaxInstallments');
      const submissionResponse: any = await helper.callApiRequest(createBulkUsersAPI, 'POST', '', formalizedUserData);
      setSaving(false);

      if (submissionResponse?.data?.status === 200) {
        createUserFormInstance.resetFields();
        message.success(submissionResponse.data.message);
        return;
      }

      setErrorsList(submissionResponse.data?.reason[0]?.errors ?? []);
    } catch (_) {
      message.error(errorMessage);
      setSaving(false);
    }
  };

  const refreshPage = () => {
    window.location.reload();
  };

  const closeErrorsModal = () => { setErrorsList([]); };

  // JSX helpers --------------------------------------------------------
  const SectionHeader = ({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) => (
    <div className="section-header">
      <span className="section-icon">{icon}</span>
      <div className="section-heading">
        <span className="section-title">{title}</span>
        <span className="section-subtitle">{subtitle}</span>
      </div>
    </div>
  );

  const nameRules: any[] = [{ required: true, message: 'User Name field is mandatory!' }];
  if (region === Region.SE) {
    nameRules.push({ pattern: validationPatterns.englishStringRegex, message: 'User Name field is not valid' });
  }

  const createUserForm = (
    <Form
      name="create"
      form={createUserFormInstance}
      onFinish={submitForm}
      layout="vertical"
      className="create-user-form"
      requiredMark="optional"
    >
      {/* Personal Information ------------------------------------------------ */}
      <div className="form-section">
        <SectionHeader
          icon={<UserOutlined />}
          title="Personal Information"
          subtitle="Basic identity details of the user"
        />
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Phone"
              name="phone"
              rules={[{ required: true, message: 'Phone field is mandatory!' }]}
            >
              <Input prefix={<PhoneOutlined className="field-icon" />} placeholder="Enter the Phone" disabled={saving} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={region === Region.SE ? 'User Name' : 'Username'}
              name="name"
              rules={nameRules}
            >
              <Input prefix={<UserOutlined className="field-icon" />} placeholder="Enter the User Name" disabled={saving} />
            </Form.Item>
          </Col>

          {region === Region.SE && (
            <Col xs={24} md={12}>
              <Form.Item
                label="City"
                name="city"
                rules={[
                  { required: true, message: 'City field is mandatory!' },
                  { pattern: validationPatterns.englishStringRegex, message: 'City field is not valid' },
                ]}
              >
                <Input prefix={<HomeOutlined className="field-icon" />} placeholder="Enter the City Name" disabled={saving} />
              </Form.Item>
            </Col>
          )}

          <Col xs={24} md={12}>
            <Form.Item label="National ID" name="nationalID">
              <Input prefix={<IdcardOutlined className="field-icon" />} placeholder="Enter the National ID" disabled={saving} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[{ pattern: validationPatterns.email, message: 'Email field is not valid' }]}
            >
              <Input prefix={<MailOutlined className="field-icon" />} placeholder="Enter the Email" disabled={saving} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Bank & Financial ---------------------------------------------------- */}
      <div className="form-section">
        <SectionHeader
          icon={<CreditCardOutlined />}
          title="Bank & Financial Details"
          subtitle="Salary and banking information"
        />
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Net Salary"
              name="netSalary"
              rules={[
                { required: true, message: 'Net Salary field is mandatory!' },
                { pattern: validationPatterns.positiveIntegerNumbers, message: 'Net Salary field is not valid' },
              ]}
            >
              <Input prefix={<DollarOutlined className="field-icon" />} type="number" placeholder="Enter the Net Salary" disabled={saving} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="bankName" label="Bank" rules={[{ required: true, message: 'Bank field is mandatory!' }]}>
              <Select
                placeholder="Select a bank"
                allowClear
                disabled={saving}
                showSearch
                suffixIcon={<BankOutlined />}
                filterOption={(input, option: any) => helper.dropDownSearch(input, option)}
              >
                {Array.isArray(banks) && banks.filter((bankData: any) => bankData.bankID).map((bankData: any, key: number) => (
                  <Option value={bankData.bankName} key={key}>{bankData.bankName}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Account Number"
              name="accountNumber"
              rules={[
                { required: true, message: 'Account Number field is mandatory!' },
                { pattern: validationPatterns.integerNumbers, message: 'Account Number field is not valid' },
              ]}
            >
              <Input prefix={<NumberOutlined className="field-icon" />} placeholder="Enter the Account Number" disabled={saving} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Account Holder Name"
              name="accountHolderName"
              rules={[
                { required: true, message: 'Account Holder Name field is mandatory!' },
                { pattern: validationPatterns.englishStringRegex, message: 'Account Holder Name field is not valid' },
              ]}
            >
              <Input prefix={<UserOutlined className="field-icon" />} placeholder="Enter the Account Holder Name" disabled={saving} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Account Holder Name (English)"
              name="accountHolderNameEn"
              rules={[
                { pattern: helper.regexValidation.englishRegex, message: 'Account name (English) must be in english' },
                { min: 3, message: `Account name (English) can't be less than 3 chars` },
                { max: 250, message: `Account name (English) can't be more than 250 chars` },
              ]}
            >
              <Input prefix={<UserOutlined className="field-icon" />} placeholder="Enter the Account Holder Name (English)" disabled={saving} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Company & Employment ------------------------------------------------ */}
      <div className="form-section">
        <SectionHeader
          icon={<TeamOutlined />}
          title="Company & Employment"
          subtitle="Where and how the user is employed"
        />
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <Form.Item name="companyName" label="Company" rules={[{ required: true, message: 'Company field is mandatory!' }]}>
              <Select
                placeholder="Select a company"
                allowClear
                disabled={saving}
                showSearch
                suffixIcon={<TeamOutlined />}
                filterOption={(input, option: any) => helper.dropDownSearch(input, option)}
              >
                {Array.isArray(companies) && companies.map((companyData: any, key: number) => (
                  <Option value={companyData.name} key={key}>{companyData.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Hiring Date" name="hiringDate">
              <DatePicker
                style={{ width: '100%' }}
                suffixIcon={<CalendarOutlined />}
                placeholder="Enter the Hiring Date"
                disabledDate={(pickerDate) => {
                  const currentDate = moment().format('YYYY-MM-DD');
                  return pickerDate && pickerDate > moment(currentDate, 'YYYY-MM-DD');
                }}
                disabled={saving}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Staff ID"
              name="staff_id"
              rules={[{ pattern: validationPatterns.englishRegex, message: 'Staff ID is not valid' }]}
            >
              <Input prefix={<IdcardOutlined className="field-icon" />} placeholder="Enter the Staff Id" disabled={saving} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Job Title"
              name="jobTitle"
              rules={[{ pattern: validationPatterns.englishRegex, message: 'Job Title field is not valid' }]}
            >
              <Input prefix={<SolutionOutlined className="field-icon" />} placeholder="Enter the job title" disabled={saving} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              className="mi-form-item"
              label="Maximum Installments"
              name="userMaxInstallments"
              extra={companyMaxInstallmentCount ? (
                <span className="mi-hint">Company max: {companyMaxInstallmentCount}</span>
              ) : undefined}
              rules={[{ type: 'integer', min: 1, max: 15, message: 'Maximum Installments must be between 1 and 15' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                precision={0}
                placeholder="Leave empty to use company default"
                disabled={saving}
              />
            </Form.Item>
          </Col>

          {region === Region.SE && (
            <Col xs={24} md={12}>
              <Form.Item
                label="Probation Period"
                name="probationPeriod"
                rules={[{ pattern: validationPatterns.positiveIntegerNumbers, message: 'Probation Period field is not valid' }]}
              >
                <Input prefix={<FieldTimeOutlined className="field-icon" />} placeholder="Enter the Probation Period" disabled={saving} />
              </Form.Item>
            </Col>
          )}

          {region === Region.EG && (
            <Col xs={24}>
              <Form.Item label="Comment" name="comment">
                <Input.TextArea rows={3} placeholder="Enter your comment" disabled={saving} />
              </Form.Item>
            </Col>
          )}
        </Row>
      </div>

      {/* Actions ------------------------------------------------------------- */}
      <div className="form-actions">
        <Button
          className="reset-button"
          shape="round"
          icon={<ReloadOutlined />}
          disabled={saving}
          onClick={() => createUserFormInstance.resetFields()}
        >
          Reset
        </Button>
        <Button
          className="submit-button"
          type="primary"
          shape="round"
          htmlType="submit"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={saving}
        >
          Create User
        </Button>
      </div>
    </Form>
  );

  // Render ----------------------------------------------------------
  return (
    <div className="create-user-container">
      {loader ? (
        <div className="create-user-loader">
          <Spinner size="large" />
        </div>
      ) : errorMode ? (
        <OutPutResult status={'500'} title={'Oops!'} subTitle={errorMessage} btnTitle={'Refresh the page'} btnClick={refreshPage} />
      ) : (
        <Card className="create-user-card" bordered={false}>
          <div className="create-user-header">
            <div className="header-avatar">
              <UserAddOutlined />
            </div>
            <div className="header-text">
              <Title level={3} className="header-title">Create New User</Title>
              <Text className="header-subtitle">Fill in the details below to onboard a single user</Text>
            </div>
          </div>

          {createUserForm}
        </Card>
      )}

      <Modal
        open={!!errorsList.length}
        footer={null}
        closable={false}
        centered
        width={540}
        wrapClassName="cu-error-wrap"
        className="cu-error-modal"
        onCancel={closeErrorsModal}
      >
        <div className="cu-error">
          <div className="cu-error-glow" />
          <button type="button" className="cu-error-close" onClick={closeErrorsModal} aria-label="Close">
            <CloseOutlined />
          </button>
          <div className="cu-error-head">
            <div className="cu-error-icon">
              <CloseCircleFilled />
            </div>
            <div className="cu-error-headtext">
              <h3 className="cu-error-title">We couldn't create the user</h3>
              <span className="cu-error-subtitle">
                {errorsList.length} {errorsList.length === 1 ? 'issue' : 'issues'} need your attention
              </span>
            </div>
          </div>
          <div className="cu-error-list">
            {errorsList.map((err: string, index: number) => (
              <div className="cu-error-item" key={index}>
                <span className="cu-error-bullet">
                  <WarningFilled />
                </span>
                <span className="cu-error-text">{err}</span>
              </div>
            ))}
          </div>
          <div className="cu-error-actions">
            <Button className="cu-error-btn" shape="round" onClick={closeErrorsModal}>
              Got it
            </Button>
          </div>
        </div>
      </Modal>

      <MaxInstallmentsWarningModal
        open={warningModal.visible}
        companyMax={warningModal.companyMax}
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
      />
    </div>
  );
}
